import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useMyPayouts } from '../hooks/usePayout';
import { getApiErrorMessage } from '../api/errors';
import { usePageTitle } from '../hooks/usePageTitle';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

const TIER_LABELS = {
  TierA_PremiumOriginal: 'Tier A — Premium Original',
  TierB_LicensedHit: 'Tier B — Licensed Hit',
  TierC_IndieNew: 'Tier C — Indie / New',
};

function nairaFmt(n) {
  return `₦${Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
}

function thisMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function StatCard({ icon, value, label }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06]">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-primary ring-1 ring-white/10">
        <Icon icon={icon} className="size-4" />
      </span>
      <div className="min-w-0">
        <div className="truncate text-lg font-semibold text-foreground">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function PayoutTitleCard({ r }) {
  return (
    <div className="rounded-lg bg-white/[0.03] p-3.5 ring-1 ring-foreground/[0.06]">
      <div className="flex items-center gap-3.5">
        {r.ThumbnailUrl ? (
          <img
            src={r.ThumbnailUrl}
            alt={r.MovieTitle}
            className="h-14 w-24 shrink-0 rounded-md object-cover ring-1 ring-white/10"
          />
        ) : (
          <div className="flex h-14 w-24 shrink-0 items-center justify-center rounded-md bg-white/5 text-muted-foreground/40 ring-1 ring-white/10">
            <Icon icon="ph:film-slate-bold" className="size-5" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{r.MovieTitle}</p>
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {TIER_LABELS[r.Tier] || r.Tier}
            {r.WasInDiscoveryWindow && (
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary ring-1 ring-primary/25">
                Discovery boost
              </span>
            )}
          </p>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-lg font-bold tabular-nums text-primary">{nairaFmt(r.FinalPayoutNaira)}</div>
          <div className="text-[11px] text-muted-foreground">this period</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/5 pt-3 text-xs text-muted-foreground sm:grid-cols-4">
        <div className="flex items-center gap-1.5">
          <Icon icon="ph:eye-bold" className="shrink-0 text-muted-foreground/50" /> {r.UniqueViewers} viewers
        </div>
        <div className="flex items-center gap-1.5">
          <Icon icon="ph:clock-bold" className="shrink-0 text-muted-foreground/50" /> {r.WatchHours} hrs watched
        </div>
        <div className="flex items-center gap-1.5">
          <Icon icon="ph:user-plus-bold" className="shrink-0 text-muted-foreground/50" /> {r.NewSubscribersAcquired} new subs
        </div>
        <div className="flex items-center gap-1.5">
          <Icon icon="ph:repeat-bold" className="shrink-0 text-muted-foreground/50" /> {r.RetentionScore}% retained
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 border-t border-white/5 pt-2.5 text-xs text-muted-foreground">
        <span>Completion: <strong className="text-foreground/80">{r.CompletionRatePercent}%</strong></span>
        <span>Rating: <strong className="text-foreground/80">{r.RatingScore}%</strong></span>
        <span>TPS: <strong className="text-foreground/80">{r.TitlePerformanceScore}</strong></span>
        <span>Pool share: <strong className="text-foreground/80">{r.PoolSharePercent}%</strong></span>
      </div>
    </div>
  );
}

export default function ProducerPayoutPage() {
  usePageTitle('My Payouts · Kast');
  const [month, setMonth] = useState(thisMonthValue());
  const [year, m] = month.split('-').map((v) => parseInt(v, 10));
  const { data, isLoading, error } = useMyPayouts(year, m);

  const results = data?.data ?? [];
  const runStatus = data?.runStatus ?? 'NotYetComputed';

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <p className="mb-6 max-w-2xl text-sm text-muted-foreground">
        Your content earns what it performs — transparent monthly breakdown, title by title.
      </p>

      <div className="mb-6 max-w-[200px]">
        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Period</label>
        <input type="month" className={inputClass} value={month} onChange={(e) => setMonth(e.target.value)} />
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : error ? (
        <EmptyState icon="ph:warning-bold">{getApiErrorMessage(error, 'Failed to load your payouts.')}</EmptyState>
      ) : runStatus === 'NotYetComputed' ? (
        <EmptyState icon="ph:info-bold">
          Payouts for this period haven't been computed yet — check back after the admin runs it.
        </EmptyState>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard icon="ph:activity-bold" value={runStatus} label="Run Status" />
            <StatCard
              icon="ph:currency-circle-dollar-bold"
              value={nairaFmt(data.totalPayoutNaira)}
              label={runStatus === 'Finalized' ? 'Total Payout' : 'Total Estimated Payout'}
            />
            <StatCard icon="ph:film-slate-bold" value={results.length} label="Titles Earning" />
          </div>

          {results.length === 0 ? (
            <EmptyState icon="ph:currency-circle-dollar-bold">None of your titles had activity in this period.</EmptyState>
          ) : (
            <div className="flex flex-col gap-2.5">
              {results.map((r) => (
                <PayoutTitleCard key={r.MovieId} r={r} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
